import React, { useState } from 'react';
import { useI18n } from '../i18n';
import { submitWaitlist } from '../lib/waitlist';
import { trackWaitlistSubmit } from '../lib/analytics';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { CheckCircle2, ShieldCheck, AlertCircle, ArrowRight, Sparkles } from 'lucide-react';

export interface WaitlistFormProps {
  source?: string;
  className?: string;
}

export const WaitlistForm: React.FC<WaitlistFormProps> = ({
  source = 'hero',
  className = '',
}) => {
  const { t, locale } = useI18n();
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'duplicate' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setStatus('error');
      setErrorMessage(t.waitlist.errorMessage);
      trackWaitlistSubmit('error', {
        source,
        locale,
        error: 'Invalid email format',
      });
      return;
    }

    setStatus('loading');
    setErrorMessage('');
    trackWaitlistSubmit('attempt', { source, locale });

    const res = await submitWaitlist({
      email: email.trim(),
      locale,
      source,
      honeypot,
    });

    if (res.success) {
      if (res.status === 'already_subscribed') {
        setStatus('duplicate');
        trackWaitlistSubmit('duplicate', { source, locale });
      } else {
        setStatus('success');
        trackWaitlistSubmit('success', { source, locale });
      }
    } else {
      const err = res.error || t.waitlist.errorMessage;
      setStatus('error');
      setErrorMessage(err);
      trackWaitlistSubmit('error', { source, locale, error: err });
    }
  };

  if (status === 'success' || status === 'duplicate') {
    return (
      <div className="w-full max-w-lg mx-auto bg-navy-850/90 dark:bg-navy-850/90 light:bg-white border border-gold-500/50 rounded-2xl p-6 shadow-gold-glow animate-fade-in text-center">
        <div className="w-12 h-12 rounded-full bg-gold-500/20 border border-gold-500/60 flex items-center justify-center mx-auto mb-3 text-gold-400">
          <CheckCircle2 className="w-7 h-7 text-gold-500" />
        </div>
        <h3 className="text-lg font-bold text-white dark:text-white light:text-slate-900 mb-1">
          {status === 'duplicate' ? t.waitlist.duplicateMessage : t.waitlist.successTitle}
        </h3>
        <p className="text-xs sm:text-sm text-slate-300 dark:text-slate-300 light:text-slate-600 mb-4 max-w-md mx-auto">
          {status === 'duplicate'
            ? t.waitlist.duplicateMessage
            : t.waitlist.successMessage}
        </p>

        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-navy-800 dark:bg-navy-800 light:bg-slate-100 border border-navy-700 dark:border-navy-700 light:border-slate-300 text-xs font-mono text-gold-400">
          <ShieldCheck className="w-4 h-4 text-gold-500" />
          <span>Priority Beta Cohort Tag: {locale.toUpperCase()}-CONFIRMED</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full max-w-xl mx-auto ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Hidden Honeypot Field */}
        <input
          type="text"
          name="workouse_hp"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          className="hidden opacity-0 pointer-events-none absolute"
          aria-hidden="true"
        />

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 p-1.5 rounded-xl bg-white dark:bg-navy-950/80 border border-slate-300 dark:border-navy-700/80 focus-within:border-gold-500 focus-within:ring-2 focus-within:ring-gold-500/20 shadow-sm dark:shadow-surface transition-all">
          <div className="flex-1 min-w-0">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === 'error') setStatus('idle');
              }}
              placeholder={t.waitlist.inputPlaceholder}
              className="bg-transparent border-0 focus:ring-0 px-3.5 py-3 text-sm placeholder:text-slate-400 dark:placeholder:text-slate-500 text-slate-900 dark:text-slate-100"
              disabled={status === 'loading'}
              autoComplete="email"
            />
          </div>

          <Button
            type="submit"
            size="md"
            isLoading={status === 'loading'}
            className="sm:w-auto w-full whitespace-nowrap px-6 py-3 text-sm font-bold shadow-gold-glow"
          >
            <span>{status === 'loading' ? t.waitlist.submitting : t.waitlist.ctaButton}</span>
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {status === 'error' && (
          <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 font-medium px-2 py-1 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg animate-fade-in">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-1 text-[11px]">
          <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5 text-amber-600 dark:text-gold-500" />
            {t.waitlist.privacyNotice}
          </span>
          <span className="text-slate-600 dark:text-gold-400/90 font-medium flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-amber-600 dark:text-gold-500" />
            {t.waitlist.statsNote}
          </span>
        </div>
      </form>
    </div>
  );
};
